import { path } from 'ramda';

/**
 * Validate phone number has 10 digits
 */
export const isValidPhone = (phone) => {
  if (!phone) return false;
  const digitsOnly = phone.replace(/\D/g, '');
  return digitsOnly.length === 10;
};

/**
 * Check if email is verified for a member
 * Handles both admin users and regular members
 */
export const isEmailVerified = (member, user) => {
  if (member.admin) {
    // Admin must have an email AND it must be verified
    return path(['info', 'email'], user) && path(['info', 'isVerified'], user);
  }
  return member.emailVerified;
};

/**
 * Check if phone is verified for a member
 * Handles both admin users and regular members
 */
export const isPhoneVerified = (member, user) => {
  if (member.admin) {
    // Admin must have a phone AND it must be verified (no pending confirmation)
    return path(['info', 'phone'], user) && !path(['info', 'phoneConfirmKey'], user);
  }
  return member.phoneVerified;
};

/**
 * Check if member has either verified email or verified phone
 */
export const hasVerifiedContact = (member, user) => {
  return (member.email && isEmailVerified(member, user)) ||
         (member.phone && isPhoneVerified(member, user));
};

/**
 * Get email invite status for a member
 * Returns: 'verified' | 'pending' | 'can_send_invite' | 'unverified'
 */
export const getEmailInviteStatus = (member, user) => {
  if (member.admin) {
    return path(['info', 'isVerified'], user) ? 'verified' : 'pending';
  }

  if (member.emailVerified) {
    return 'verified';
  }

  if (member.emailInvitePending || member.emailUaId || (member.uaId && member.inviteMethod === 'email')) {
    return 'pending';
  }

  // SMS users with verified phone who don't have a pending email verification
  if (member.inviteMethod === 'sms' && member.phoneVerified && !member.emailVerified && !member.emailInvitePending) {
    return 'can_send_invite';
  }

  return 'unverified';
};

/**
 * Get phone invite status for a member
 * Returns: 'verified' | 'pending' | 'can_send_invite' | 'unverified'
 */
export const getPhoneInviteStatus = (member, user) => {
  if (member.admin) {
    // Admin must have a phone AND it must be verified (no pending confirmation)
    return (path(['info', 'phone'], user) && !path(['info', 'phoneConfirmKey'], user)) ? 'verified' : 'unverified';
  }

  if (member.phoneVerified) {
    return 'verified';
  }

  if (member.phoneInvitePending || member.smsUaId || (member.uaId && member.inviteMethod === 'sms')) {
    return 'pending';
  }

  // Email users with verified email who don't have a pending phone verification
  if (member.inviteMethod === 'email' && member.emailVerified && !member.phoneVerified && !member.phoneInvitePending) {
    return 'can_send_invite';
  }

  return 'unverified';
};

/**
 * Create invite action object for user action
 * @param {string} userActionTypePrefix - e.g., 'alert-set-group-member' or 'heat-stress-group-member'
 * @param {string} method - 'email' or 'sms'
 * @param {string} memberId - Member ID
 * @param {string} contactInfo - Email address or phone number
 * @param {string} name - Member name
 */
export const createInviteAction = (userActionTypePrefix, method, memberId, contactInfo, name) => ({
  type: `${userActionTypePrefix}-${method}`,
  data: {
    memberId,
    ...(method === 'email'
      ? { email: contactInfo }
      : { phone: contactInfo }),
    name: name || 'Group Member'
  }
});

/**
 * Determine if email field should be shown in editing mode
 */
export const shouldShowEmailField = (member) => {
  return member.inviteMethod === 'email' ||
         member.email ||
         (member.inviteMethod === 'sms' && member.phoneVerified);
};

/**
 * Determine if phone field should be shown in editing mode
 */
export const shouldShowPhoneField = (member) => {
  return member.inviteMethod === 'sms' ||
         member.phone ||
         (member.inviteMethod === 'email' && member.emailVerified);
};

/**
 * Helper to validate if contact method is valid based on invite method
 */
export const hasValidContact = (member, formState) => {
  // For email invites, require email
  if (member.inviteMethod === 'email') {
    return formState.email && formState.email.trim() !== '';
  }
  // For SMS invites, require valid phone
  else if (member.inviteMethod === 'sms') {
    return formState.phone && formState.phone.trim() !== '' && isValidPhone(formState.phone);
  }
  // For existing or non-specified invites, check if either is valid
  else {
    return (formState.email && formState.email.trim() !== '') ||
           (formState.phone && formState.phone.trim() !== '' && isValidPhone(formState.phone));
  }
};

/**
 * Centralized validation function
 */
export const validateForm = (member, formState) => {
  // For email invites, require email
  if (member.inviteMethod === 'email') {
    if (!formState.email) {
      return 'Email address is required';
    }
  }
  // For SMS invites, require valid phone
  else if (member.inviteMethod === 'sms') {
    if (!formState.phone) {
      return 'Phone number is required';
    } else if (!isValidPhone(formState.phone)) {
      return 'Phone number must have 10 digits';
    }
  }
  // For existing or non-specified invites, check if either is valid
  else {
    if (!formState.email && (!formState.phone || !isValidPhone(formState.phone))) {
      return 'Either email or valid 10-digit phone number is required';
    } else if (formState.phone && formState.phone.trim() !== '' && !isValidPhone(formState.phone)) {
      return 'Phone number must have 10 digits';
    }
  }

  return ''; // No error
};
